/**
 * Cascader 横向级联选择器组件（优化重构版）
 * 基于 jQuery 开发
 * 优化：减少DOM操作、避免不必要的重绘、事件委托、缓存优化
 */
(function (window, $) {
  "use strict";

  // 全局实例存储（用于销毁）
  var instances = (window.__cascaderInstances = window.__cascaderInstances || {});
  var instanceId = 0;

  function Cascader(options) {
    // 基础配置
    this.$html = $("<div>")
      .addClass("cascader-container")
      .css({ height: options.panelHeight || "auto" });
    this.data = options.data || [];
    this.multiple = options.multiple || false;
    this.onlyLeaf = options.onlyLeaf !== false;
    this.expandTrigger = options.expandTrigger || "click";
    this.panelWidth = options.panelWidth || null; // 每个面板的宽度

    // 实例 ID
    this.instanceId = "cascader_" + ++instanceId;
    instances[this.instanceId] = this;

    // 状态管理
    this.selectedIds = new Set(); // 使用 Set 存储选中的 ID，查找更快
    this.selectedMap = new Map(); // 存储完整的选中信息 {id: {id, name, path}}
    this.activePath = []; // 当前激活的路径

    // 缓存
    this.itemMap = new Map(); // 缓存所有 item: id -> item
    this.parentMap = new Map(); // 缓存父子关系: childId -> parentId
    this.childrenMap = new Map(); // 缓存子节点: parentId -> [childIds]

    // DOM 缓存
    this.$panels = null;
    this.hoverTimer = null; // hover 防抖定时器

    // 事件发射器
    this.emitter = window.useEmitter();

    // 初始化
    this.init(options);
  }

  // ==================== 初始化 ====================

  Cascader.prototype.init = function (options) {
    this.buildCache();
    this.render();

    // 设置初始值
    var initialValue = options.value !== undefined ? options.value : options.defaultValue;
    if (initialValue) {
      this.setValue(initialValue);
    }
  };

  // 构建缓存，提高查询效率
  Cascader.prototype.buildCache = function () {
    var self = this;

    function traverse(items, parentId) {
      items.forEach(function (item) {
        // 统一转换为字符串，避免类型不匹配问题
        var itemIdStr = String(item.id);

        // 缓存 item
        self.itemMap.set(itemIdStr, item);

        // 缓存父子关系
        if (parentId !== null) {
          self.parentMap.set(itemIdStr, String(parentId));
        }

        // 缓存子节点
        if (item.children && item.children.length > 0) {
          var childIds = item.children.map(function (child) {
            return String(child.id);
          });
          self.childrenMap.set(itemIdStr, childIds);
          traverse(item.children, item.id);
        }
      });
    }

    traverse(this.data, null);
  };

  // ==================== 数据操作 ====================

  Cascader.prototype.updateData = function (data) {
    this.data = data;
    this.selectedIds.clear();
    this.selectedMap.clear();
    this.activePath = [];
    this.itemMap.clear();
    this.parentMap.clear();
    this.childrenMap.clear();

    this.buildCache();
    this.render();
  };

  // 获取所有子节点 ID（递归）
  Cascader.prototype.getAllChildrenIds = function (itemId) {
    var result = [];
    var childIds = this.childrenMap.get(itemId);

    if (childIds && childIds.length > 0) {
      childIds.forEach(function (childId) {
        result.push(childId);
        result = result.concat(this.getAllChildrenIds(childId));
      }, this);
    }

    return result;
  };

  // 检查是否所有子节点都被选中
  Cascader.prototype.isAllChildrenSelected = function (itemId) {
    var childIds = this.childrenMap.get(itemId);
    if (!childIds || childIds.length === 0) return false;

    var allChildIds = this.getAllChildrenIds(itemId);
    return allChildIds.every(function (id) {
      return this.selectedIds.has(id);
    }, this);
  };

  // 检查是否有部分子节点被选中
  Cascader.prototype.isSomeChildrenSelected = function (itemId) {
    var childIds = this.childrenMap.get(itemId);
    if (!childIds || childIds.length === 0) return false;

    var allChildIds = this.getAllChildrenIds(itemId);
    return allChildIds.some(function (id) {
      return this.selectedIds.has(id);
    }, this);
  };

  // 选中节点及所有子节点
  Cascader.prototype.selectWithChildren = function (itemId, path) {
    var item = this.itemMap.get(itemId);
    if (!item) return;

    var allIds = [itemId].concat(this.getAllChildrenIds(itemId));

    allIds.forEach(function (id, index) {
      var targetItem = this.itemMap.get(id);
      var targetPath = index === 0 ? path : this.getItemPath(id);

      this.selectedIds.add(id);
      this.selectedMap.set(id, {
        id: id,
        name: targetItem.name,
        path: targetPath,
      });
    }, this);
  };

  // 取消选中节点及所有子节点
  Cascader.prototype.unselectWithChildren = function (itemId) {
    var allIds = [itemId].concat(this.getAllChildrenIds(itemId));

    allIds.forEach(function (id) {
      this.selectedIds.delete(id);
      this.selectedMap.delete(id);
    }, this);
  };

  // 获取节点的完整路径
  Cascader.prototype.getItemPath = function (itemId) {
    var path = [];
    var currentId = itemId;

    while (currentId !== undefined && currentId !== null) {
      path.unshift(currentId);
      currentId = this.parentMap.get(currentId);
    }

    return path;
  };

  // ==================== 选择逻辑 ====================

  Cascader.prototype.handleItemClick = function (itemId, level) {
    var item = this.itemMap.get(itemId);
    if (!item) return;

    var newPath = this.activePath.slice(0, level).concat([itemId]);
    var hasChildren = this.childrenMap.has(itemId);
    var canSelect = !this.onlyLeaf || !hasChildren;
    var isSelected = this.selectedIds.has(itemId);

    // 处理选择
    if (canSelect) {
      if (this.multiple) {
        this.handleMultipleSelect(itemId, newPath, hasChildren, isSelected);
      } else {
        this.handleSingleSelect(itemId, newPath, isSelected);
      }

      // 只更新选中状态的DOM，不重新渲染整个组件，不重新绑定事件
      this.updateSelectState();
      this.emitter.emit("change", this.getValue());
      return;
    }

    // 处理展开（仅 onlyLeaf 模式下点击父节点时展开）
    if (hasChildren && this.expandTrigger === "click" && this.onlyLeaf) {
      // 检查路径是否真的改变了
      var pathChanged = false;
      if (newPath.length !== this.activePath.length) {
        pathChanged = true;
      } else {
        for (var i = 0; i < newPath.length; i++) {
          if (newPath[i] !== this.activePath[i]) {
            pathChanged = true;
            break;
          }
        }
      }

      // 只在路径真正改变时才重新渲染
      if (pathChanged) {
        this.activePath = newPath;
        this.renderPanels();
        this.updateSelectState();
      }
    }
  };

  // 多选处理
  Cascader.prototype.handleMultipleSelect = function (itemId, path, hasChildren, isSelected) {
    if (hasChildren) {
      var allChildrenSelected = this.isAllChildrenSelected(itemId);

      if (allChildrenSelected || isSelected) {
        this.unselectWithChildren(itemId);
        // 取消选中后，向上更新父节点状态
        this.updateParentStatesAfterChange();
      }else {
        this.selectWithChildren(itemId, path);
        // 选中后，向上更新父节点状态
        this.updateParentStatesAfterChange();
      }
    } else {
      if (isSelected) {
        this.selectedIds.delete(itemId);
        this.selectedMap.delete(itemId);
        // 取消选中后，向上更新父节点状态
        this.updateParentStatesAfterChange();
      } else {
        var item = this.itemMap.get(itemId);
        this.selectedIds.add(itemId);
        this.selectedMap.set(itemId, {
          id: itemId,
          name: item.name,
          path: path,
        });
        // 选中后，向上更新父节点状态
        this.updateParentStatesAfterChange();
      }
    }
  };

  // 单选处理
  Cascader.prototype.handleSingleSelect = function (itemId, path, isSelected) {
    if (isSelected) {
      this.selectedIds.clear();
      this.selectedMap.clear();
      this.activePath = [];
    } else {
      var item = this.itemMap.get(itemId);
      var hasChildren = this.childrenMap.has(itemId);

      this.selectedIds.clear();
      this.selectedMap.clear();
      this.selectedIds.add(itemId);
      this.selectedMap.set(itemId, {
        id: itemId,
        name: item.name,
        path: path,
      });

      // 单选模式下，叶子节点才设置 activePath
      if (!hasChildren) {
        this.activePath = path;
      }
    }
  };

  // Hover 处理（带防抖）
  Cascader.prototype.handleItemHover = function (itemId, level) {
    var self = this;

    // // 清除之前的定时器
    // if (this.hoverTimer) {
    //   clearTimeout(this.hoverTimer);
    // }

    // // 防抖：100ms 后再展开
    // this.hoverTimer = setTimeout(function () {
    var hasChildren = self.childrenMap.has(itemId);
    var newPath;

    if (hasChildren) {
      // 有子节点，展开子节点
      newPath = self.activePath.slice(0, level).concat([itemId]);
    } else {
      // 没有子节点，截断路径到当前层级
      newPath = self.activePath.slice(0, level);
    }

    // 检查路径是否真的改变了，避免不必要的重新渲染
    var pathChanged = false;
    if (newPath.length !== self.activePath.length) {
      pathChanged = true;
    } else {
      for (var i = 0; i < newPath.length; i++) {
        if (newPath[i] !== self.activePath[i]) {
          pathChanged = true;
          break;
        }
      }
    }

    // 只在路径真正改变时才重新渲染
    if (pathChanged) {
      self.activePath = newPath;
      self.renderPanels();
      self.updateSelectState();
    }
    // }, 100);
  };

  // ==================== 获取/设置值 ====================

  Cascader.prototype.getValue = function () {
    if (!this.multiple) {
      if (this.selectedMap.size === 0) return null;
      var value = Array.from(this.selectedMap.values())[0];
      return {
        id: value.id,
        path: value.path,
        name: value.name,
      };
    }

    // 多选模式：
    // 1. 如果父节点的所有子节点都被选中，只返回父节点
    // 2. 如果父节点只有部分子节点被选中，只返回被选中的子节点，不返回父节点
    var result = [];
    var processedIds = new Set();
    var self = this;

    function processItem(itemId) {
      if (processedIds.has(itemId)) return;

      var hasChildren = self.childrenMap.has(itemId);
      var isSelected = self.selectedIds.has(itemId);

      if (hasChildren) {
        var allChildrenSelected = self.isAllChildrenSelected(itemId);

        if (allChildrenSelected) {
          // 所有子节点都被选中，只返回父节点
          var value = self.selectedMap.get(itemId);
          if (value) {
            result.push(value);
          }
          processedIds.add(itemId);

          // 标记所有子节点为已处理
          var allChildIds = self.getAllChildrenIds(itemId);
          allChildIds.forEach(function (id) {
            processedIds.add(id);
          });
        }else {
          // 部分子节点被选中，只返回被选中的子节点
          // 不返回父节点本身
          processedIds.add(itemId);

          // 递归处理子节点
          var childIds = self.childrenMap.get(itemId);
          if (childIds) {
            childIds.forEach(function (childId) {
              processItem(childId);
            });
          }
        }
      } else {
        // 叶子节点
        if (isSelected && !processedIds.has(itemId)) {
          var value = self.selectedMap.get(itemId);
          result.push(value);
          processedIds.add(itemId);
        }
      }
    }

    // 从根节点开始处理
    this.data.forEach(function (item) {
      processItem(String(item.id)); // 转换为字符串
    });

    return result;
  };

  Cascader.prototype.setValue = function (value) {
    var self = this;

    if (!value) {
      // 如果传入空值，清空所有选中
      if (this.selectedIds.size > 0) {
        this.selectedIds.clear();
        this.selectedMap.clear();
        this.activePath = [];
        this.renderPanels();
        this.updateSelectState();
      }
      return;
    }

    var values = Array.isArray(value) ? value : [value];

    // 收集新的选中项
    var newSelectedIds = new Set();
    var newSelectedMap = new Map();
    var maxPath = [];

    values.forEach(function (val) {
      if (!val || !val.path) return;

      // 确保 path 中的所有 ID 都是字符串
      var pathStr = val.path.map(function (id) {
        return String(id);
      });
      var itemId = pathStr[pathStr.length - 1];
      var item = self.itemMap.get(itemId);

      if (item) {
        var hasChildren = self.childrenMap.has(itemId);

        // 检查 onlyLeaf 模式
        if (self.onlyLeaf && hasChildren) {
          console.warn("Cascader [Warning] => Cannot select parent node in onlyLeaf mode:", itemId);
          return;
        }

        // 多选模式下，如果选中父节点，需要联动选中所有子节点
        if (self.multiple && hasChildren) {
          var allIds = [itemId].concat(self.getAllChildrenIds(itemId));
          allIds.forEach(function (id) {
            var targetItem = self.itemMap.get(id);
            var targetPath = self.getItemPath(id);

            newSelectedIds.add(id);
            newSelectedMap.set(id, {
              id: id,
              name: targetItem.name,
              path: targetPath,
            });
          });
        }else {
          // 单选或叶子节点
          newSelectedIds.add(itemId);
          newSelectedMap.set(itemId, {
            id: itemId,
            name: item.name,
            path: pathStr,
          });
        }

        // 保存最长的路径作为 activePath（用于展示面板）
        if (pathStr.length > maxPath.length) {
          maxPath = pathStr.slice();
        }
      }
    });

    // 多选模式下，检查父节点状态（向上联动）
    if (this.multiple) {
      this.updateParentStates(newSelectedIds, newSelectedMap);
    }

    // 对比差异：找出需要取消选中的项
    var toRemove = [];
    this.selectedIds.forEach(function (id) {
      if (!newSelectedIds.has(id)) {
        toRemove.push(id);
      }
    });

    // 对比差异：找出需要新增选中的项
    var toAdd = [];
    newSelectedIds.forEach(function (id) {
      if (!self.selectedIds.has(id)) {
        toAdd.push(id);
      }
    });

    // 如果没有任何变化，直接返回
    if (toRemove.length === 0 && toAdd.length === 0) {
      return;
    }

    // 执行取消选中
    toRemove.forEach(function (id) {
      self.selectedIds.delete(id);
      self.selectedMap.delete(id);
    });

    // 执行新增选中
    toAdd.forEach(function (id) {
      self.selectedIds.add(id);
      self.selectedMap.set(id, newSelectedMap.get(id));
    });

    // 更新 activePath（展开到选中的节点）
    var needRenderPanels = false;
    if (maxPath.length > 0) {
      // 展开到选中节点的子面板
      // - 如果选中根节点，activePath = [根节点ID]，显示根节点的子面板
      // - 如果选中子节点，activePath = 到该节点的完整路径，显示该节点的子面板
      var newActivePath = maxPath.slice();

      // 检查 activePath 是否需要更新
      var pathChanged = false;
      if (newActivePath.length !== self.activePath.length) {
        pathChanged = true;
      } else {
        for (var i = 0; i < newActivePath.length; i++) {
          if (newActivePath[i] !== self.activePath[i]) {
            pathChanged = true;
            break;
          }
        }
      }

      if (pathChanged) {
        self.activePath = newActivePath;
        needRenderPanels = true;
      }
    }

    // 如果 activePath 改变了，需要重新渲染面板
    if (needRenderPanels) {
      this.renderPanels();
    }

    // 更新选中状态（不重新渲染面板结构）
    this.updateSelectState();
  };

  // 向上更新父节点状态（当子节点全选时，父节点也应该被选中）
  Cascader.prototype.updateParentStates = function (selectedIds, selectedMap) {
    var self = this;
    var needUpdate = true;

    // 多次遍历，直到没有新的父节点被选中为止
    while (needUpdate) {
      needUpdate = false;

      // 遍历所有有子节点的项
      this.childrenMap.forEach(function (childIds, parentId) {
        // 如果父节点已经被选中，跳过
        if (selectedIds.has(parentId)) return;

        // 检查所有直接子节点是否都被选中
        var allChildrenSelected = childIds.every(function (childId) {
          return selectedIds.has(childId);
        });

        if (allChildrenSelected) {
          // 所有子节点都被选中，父节点也应该被选中
          var parentItem = self.itemMap.get(parentId);
          var parentPath = self.getItemPath(parentId);

          selectedIds.add(parentId);
          selectedMap.set(parentId, {
            id: parentId,
            name: parentItem.name,
            path: parentPath,
          });

          needUpdate = true; // 标记需要继续检查上层
        }
      });
    }
  };

  // 点击选择后，向上更新父节点状态（直接操作 this.selectedIds 和 this.selectedMap）
  Cascader.prototype.updateParentStatesAfterChange = function () {
    var self = this;
    var needUpdate = true;

    // 多次遍历，直到没有新的父节点被选中为止
    while (needUpdate) {
      needUpdate = false;

      // 遍历所有有子节点的项
      this.childrenMap.forEach(function (childIds, parentId) {
        var allChildrenSelected = childIds.every(function (childId) {
          return self.selectedIds.has(childId);
        });

        if (allChildrenSelected) {
          // 所有子节点都被选中
          if (!self.selectedIds.has(parentId)) {
            // 父节点未被选中，需要选中
            var parentItem = self.itemMap.get(parentId);
            var parentPath = self.getItemPath(parentId);

            self.selectedIds.add(parentId);
            self.selectedMap.set(parentId, {
              id: parentId,
              name: parentItem.name,
              path: parentPath,
            });

            needUpdate = true; // 标记需要继续检查上层
          }
        } else {
          // 不是所有子节点都被选中
          if (self.selectedIds.has(parentId)) {
            // 但父节点被选中了，需要取消选中父节点
            self.selectedIds.delete(parentId);
            self.selectedMap.delete(parentId);
            needUpdate = true;
          }
        }
      });
    }
  };

  // ==================== 渲染优化 ====================

  // 完整渲染
  Cascader.prototype.render = function () {
    this.renderPanels();
    this.updateSelectState();
  };

  // 只渲染面板结构（展开/收起时）- 优化版：增量更新
  Cascader.prototype.renderPanels = function () {
    var panels = this.getPanelsData();

    // 如果没有 $panels 容器，则全量渲染
    if (!this.$panels || this.$panels.length === 0) {
      this.renderPanelsFull(panels);
      return;
    }

    var currentPanelCount = this.$panels.length;
    var targetPanelCount = panels.length;

    // 情况1：面板数量相同
    if (currentPanelCount === targetPanelCount) {
      // 虽然数量相同，但最后一个面板的内容可能需要更新
      // （例如从一个父节点 hover 到另一个父节点）
      if (targetPanelCount > 0) {
        var lastPanelIndex = targetPanelCount - 1;
        var $lastPanel = this.$panels.eq(lastPanelIndex);
        var $lastPanelList = $lastPanel.find(".cascader-panel-list");

        // 清空并重新渲染最后一个面板
        $lastPanelList.empty();
        this.renderPanelItems(panels[lastPanelIndex], lastPanelIndex, $lastPanelList);
      }
      return;
    }

    // 情况2：需要增加面板
    if (targetPanelCount > currentPanelCount) {
      var $panelsContainer = this.$html.find(".cascader-panels");

      // 只添加新的面板
      for (var i = currentPanelCount; i < targetPanelCount; i++) {
        var $panel = $('<div class="cascader-panel"></div>');

        // 如果设置了 panelWidth，应用到每个面板
        if (this.panelWidth) {
          $panel.css({
            width: this.panelWidth,
            flex: "0 0 auto", // 禁用 flex 伸缩，使用固定宽度
          });
        }

        var $list = $('<div class="cascader-panel-list"></div>');

        // 渲染面板项（会直接绑定事件）
        this.renderPanelItems(panels[i], i, $list);

        $panel.append($list);
        $panelsContainer.append($panel);
      }

      // 更新缓存
      this.$panels = this.$html.find(".cascader-panel");
    }
    // 情况3：需要减少面板
    else {
      // 移除多余的面板
      for (var j = currentPanelCount - 1; j >= targetPanelCount; j--) {
        this.$panels.eq(j).remove();
      }

      // 更新缓存
      this.$panels = this.$html.find(".cascader-panel");
    }
  };

  // 全量渲染面板（首次渲染或重置时使用）
  Cascader.prototype.renderPanelsFull = function (panels) {
    // 创建面板容器
    var $panelsContainer = $('<div class="cascader-panels"></div>');

    panels.forEach(function (panelData, level) {
      var $panel = $('<div class="cascader-panel"></div>');

      // 如果设置了 panelWidth，应用到每个面板
      if (this.panelWidth) {
        $panel.css({
          width: this.panelWidth,
          flex: "0 0 auto", // 禁用 flex 伸缩，使用固定宽度
        });
      }

      var $list = $('<div class="cascader-panel-list"></div>');

      // 渲染面板项（会直接绑定事件）
      this.renderPanelItems(panelData, level, $list);

      $panel.append($list);
      $panelsContainer.append($panel);
    }, this);

    this.$html.html($panelsContainer);
    this.$panels = this.$html.find(".cascader-panel");
  };

  // 只更新选中状态（选择时）- 关键优化点
  Cascader.prototype.updateSelectState = function () {
    var self = this;

    // 找到所有项
    this.$html.find(".cascader-item").each(function () {
      var $item = $(this);
      var itemId = String($item.attr("data-cascader-id")); // 使用 attr 获取
      var isSelected = self.selectedIds.has(itemId);
      var hasChildren = self.childrenMap.has(itemId);
      var allChildrenSelected = hasChildren && self.isAllChildrenSelected(itemId);
      var someChildrenSelected =
        hasChildren && !allChildrenSelected && self.isSomeChildrenSelected(itemId);

      // 更新 active 状态（展开路径中的项）
      var isActive = self.activePath.indexOf(itemId) !== -1;
      if (isActive) {
        $item.addClass("active");
      } else {
        $item.removeClass("active");
      }

      // 更新选中状态的 className
      // 1. 如果节点本身被选中，添加 "selected" class
      // 2. 如果节点有部分子节点被选中（但本身未选中），添加 "has-selected-children" class
      if (isSelected) {
        $item.addClass("selected");
        $item.removeClass("has-selected-children");
      } else if (someChildrenSelected) {
        $item.addClass("has-selected-children");
        $item.removeClass("selected");
      }else {
        $item.removeClass("selected");
        $item.removeClass("has-selected-children");
      }

      // 更新图标
      var $icon = $item.find(".cascader-check-icon");
      var iconClass = "";
      var iconColor = "#ededed";

      if (self.multiple) {
        if (allChildrenSelected) {
          // 所有子节点都被选中（全选状态）
          iconClass = "icon-icon-weixuanzhongxiang1";
          iconColor = "var(--color-primary-product)";
        } else if (someChildrenSelected) {
          // 部分子节点被选中（半选状态）
          iconClass = "icon-a-lianji11";
          iconColor = "var(--color-primary-product)";
        } else if (isSelected) {
          // 节点本身被选中（无论是否有子节点）
          iconClass = "icon-icon-weixuanzhongxiang1";
          iconColor = "var(--color-primary-product)";
        } else {
          // 未选中
          iconClass = "icon-icon-weixuanzhongxiang2";
        }
      } else {
        if (isSelected) {
          iconClass = "icon-icon-xuanzhongxiang";
          iconColor = "var(--color-primary-product)";
        }else {
          iconClass = "icon-weixuanzhong";
        }
      }

      $icon.attr("class", "iconfont cascader-check-icon " + iconClass);
      $icon.css("color", iconColor);
    });
  };

  // 获取面板数据
  Cascader.prototype.getPanelsData = function () {
    var panels = [this.data];

    for (var i = 0; i < this.activePath.length; i++) {
      var itemId = this.activePath[i];
      var item = this.itemMap.get(itemId);

      if (item && item.children && item.children.length > 0) {
        panels.push(item.children);
      } else {
        break;
      }
    }

    return panels;
  };

  // 渲染面板项 - 直接创建 jQuery 对象并绑定事件
  Cascader.prototype.renderPanelItems = function (data, level, $panel) {
    if (!data || data.length === 0) return;

    var self = this;

    data.forEach(function (item) {
      var itemIdStr = String(item.id); // 统一转换为字符串
      var hasChildren = self.childrenMap.has(itemIdStr);
      var isActive = self.activePath[level] == itemIdStr;
      var activeClass = isActive ? "active" : "";

      // 创建 DOM 元素
      var $item = $('<div class="cascader-item ' + activeClass + '"></div>');
      $item.attr("data-cascader-id", itemIdStr);

      // 创建图标
      var $icon = $('<i class="iconfont cascader-check-icon"></i>');
      $item.append($icon);

      // 创建标签
      var $label = $('<span class="cascader-item-label">' + item.name + "</span>");
      $item.append($label);

      // 创建箭头
      if (hasChildren) {
        var $arrow = $('<i class="iconfont icon-xiala1 cascader-arrow"></i>');
        $item.append($arrow);
      }

      // 直接绑定点击事件
      $item.on("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        self.handleItemClick(itemIdStr, level);
      });

      // 直接绑定 hover 事件
      if (self.expandTrigger === "hover") {
        $item.on("mouseenter", function () {
          self.handleItemHover(itemIdStr, level);
        });
      }

      // 添加到面板
      $panel.append($item);
    });
  };

  // ==================== 销毁 ====================

  Cascader.prototype.destroy = function () {
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
    }

    this.$html.off("click.cascader mouseenter.cascader");
    this.$html.empty();
    this.$html.removeClass("cascader-container");

    this.itemMap.clear();
    this.parentMap.clear();
    this.childrenMap.clear();
    this.selectedIds.clear();
    this.selectedMap.clear();
  };

  // 导出
  window.Cascader = Cascader;
})(window, jQuery);
